import React, {FC, useState} from "react";
import {Button, Modal, message} from "antd";
import {RollbackOutlined} from "@ant-design/icons";
import i18n from "../../services/i18n";
import RefundPanel, {RefundPanelRow, RefundChoice, isMollieRefundable} from "../RefundPanel/RefundPanel";
import {useRefundMolliePaymentFromAdminMutation} from "../../generated/graphql";
import {useCurrency} from "../../helpers/currencyHelper";
import {CurrencyShort, renderAmount} from "../../helpers/renderAmount";

// Phase 14 / Plan 14-05 (D-13, D-14, D-15): Standalone-Refund-Button.
// Sichtbar bei Mollie-Provider + bezahlter SP + refundedAmount < paymentAmount.
// Öffnet Modal mit RefundPanel mode="single" (Plan 14-04 View).

export type MollieRefundButtonProps = {
    scheduledPayment: RefundPanelRow;
    onRefunded: () => void;
    size?: "small" | "middle" | "large";
    // Optional: mehrere SPs im selben Modal zeigen (z.B. "Refund für Buchung"
    // öffnet Panel mit allen refund-fähigen SPs).
    additionalScheduledPayments?: RefundPanelRow[];
    label?: string;
};

const MollieRefundButton: FC<MollieRefundButtonProps> = ({
    scheduledPayment,
    onRefunded,
    size = "small",
    additionalScheduledPayments,
    label,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [refundMutation, {loading}] = useRefundMolliePaymentFromAdminMutation();
    const currency = useCurrency();

    // D-15 Sichtbarkeits-Check — identisch zur Zeilen-Logik im RefundPanel
    // (`isMollieRefundable`: Mollie-Provider + bezahlt + Refund-Cap > 0).
    // Der Cap basiert auf `molliePaidAmount ?? paymentAmount`; die frühere
    // naive Prüfung `refundedAmount >= paymentAmount` hat den Button auch dann
    // gezeigt, wenn bei Mollie weniger gezahlt und dieser Betrag bereits voll
    // erstattet war — das Panel wäre dann leer geblieben (Ghost-Row).
    // Der Waitlist-Fall (bookingType Waitlist) wird bewusst NICHT gesondert
    // behandelt: eine bezahlte Mollie-Rate ist unabhängig vom Buchungstyp
    // erstattbar.
    if (!isMollieRefundable(scheduledPayment)) return null;

    const rows: RefundPanelRow[] = additionalScheduledPayments && additionalScheduledPayments.length > 0
        ? [scheduledPayment, ...additionalScheduledPayments]
        : [scheduledPayment];

    const handleConfirm = async ({
        refunds,
    }: {
        refunds: Array<{scheduledPaymentId: string; amount: number; kind: RefundChoice}>;
        reason: string;
    }) => {
        // Leere Auswahl schützt RefundPanel selbst (Submit-Button disabled), aber sicherheitshalber:
        if (refunds.length === 0) {
            setModalOpen(false);
            return;
        }
        try {
            let totalAmount = 0;
            for (const r of refunds) {
                await refundMutation({
                    variables: {
                        scheduledPaymentId: r.scheduledPaymentId,
                        amount: r.amount,
                        // reason bleibt leer fuer diesen Loop — RefundPanel sendet gemeinsamen
                        // Grund via onConfirm.reason, wird auf erster API-Call gemapped.
                    },
                });
                totalAmount += r.amount;
            }
            message.success(
                i18n.views.PanelCardPaymentData.refundSuccess({amount: renderAmount({amount: totalAmount, currency: currency.short as CurrencyShort, short: true})}),
                3,
            );
            setModalOpen(false);
            onRefunded();
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            message.error(
                i18n.views.PanelCardPaymentData.refundError({error: msg}),
                5,
            );
        }
    };

    const buttonLabel = label ?? i18n.views.PanelCardPaymentData.refundButton();

    return (
        <>
            <Button
                size={size}
                icon={<RollbackOutlined />}
                onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(true);
                }}
                data-testid={`mollie-refund-button-${scheduledPayment.scheduledPaymentId}`}
            >
                {buttonLabel}
            </Button>
            <Modal
                title={i18n.views.PanelCardPaymentData.refundModalTitle()}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={null}
                destroyOnClose
                width={760}
            >
                <RefundPanel
                    mode="single"
                    scheduledPayments={rows}
                    onConfirm={handleConfirm}
                    onCancel={() => setModalOpen(false)}
                    loading={loading}
                />
            </Modal>
        </>
    );
};

export default MollieRefundButton;
